import React from "react";
import { useQuery } from "@tanstack/react-query";
import { TEAM_INFO } from "../lib/teamMap";
import { getTeams } from "../lib/api";
import { TeamBadge } from "./TeamBadge";

// Reusable PWHL team-pill selector (logo + abbreviation), matching the
// Leaderboards style. Drives a single-select team filter. Selected pills use
// the team's LIVE main (primary) colour so admin colour edits show everywhere.
export function PwhlTeamButtons({
    teamIds = [],
    selected,
    onSelect,
    allLabel = "All Teams",
    allValue = "",
    showAll = true,
    favTeam = null,
    align = "center",
    leadingExtra = null,
    leagueLabel = null,
    modeLabel = null,
    showFavourite = true,
    showAbbr = true,
    testId = "pwhl-team-buttons",
}) {
    const { data: teams = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams, staleTime: 5 * 60 * 1000 });
    const colorOf = (id) => {
        const t = teams.find((x) => x.id === id);
        return t?.primary_color || TEAM_INFO[id]?.color || "#9D4CDD";
    };
    const showFav = showFavourite && favTeam && teamIds.includes(favTeam);
    return (
        <div className={`flex flex-wrap gap-1.5 items-center ${align === "center" ? "justify-center" : ""}`} data-testid={testId}>
            {(leagueLabel || modeLabel) && (
                <span className="inline-flex items-center gap-1.5 pr-1.5 mr-0.5 border-r border-white/10 select-none" data-testid={`${testId}-league-tag`}>
                    {leagueLabel && <span className="font-heading text-sm font-black uppercase tracking-tight text-white leading-none">{leagueLabel}</span>}
                    {modeLabel && <span className="text-[9px] font-bold uppercase tracking-wider text-white/45 border border-white/15 rounded px-1.5 py-0.5 leading-none">{modeLabel}</span>}
                </span>
            )}
            {leadingExtra}
            {showAll && (
                <button
                    type="button"
                    onClick={(e) => onSelect(allValue, e)}
                    data-testid={`${testId}-all`}
                    className={`px-3.5 py-2 text-[11px] sm:text-xs font-bold uppercase tracking-wider rounded-lg border transition-colors ${selected === allValue ? "bg-[#9D4CDD] text-white border-[#9D4CDD]" : "bg-black/25 border-white/15 text-[#94A3B8] hover:text-white"}`}
                >
                    {allLabel}
                </button>
            )}
            {showFav && (
                <button
                    type="button"
                    onClick={(e) => onSelect(favTeam, e)}
                    data-testid={`${testId}-favourite`}
                    style={selected === favTeam ? { background: colorOf(favTeam), borderColor: colorOf(favTeam) } : { borderColor: `${colorOf(favTeam)}66` }}
                    className={`px-3.5 py-2 text-[11px] sm:text-xs font-bold uppercase tracking-wider rounded-lg border transition-colors ${selected === favTeam ? "text-white" : "bg-black/25 text-[#94A3B8] hover:text-white"}`}
                >
                    Favourite
                </button>
            )}
            {teamIds.map((t) => {
                const active = selected === t;
                const col = colorOf(t);
                return (
                    <button
                        key={t}
                        type="button"
                        onClick={(e) => onSelect(active ? allValue : t, e)}
                        data-testid={`${testId}-${t}`}
                        title={TEAM_INFO[t]?.name || t}
                        style={active ? { background: `${col}26`, borderColor: `${col}59` } : undefined}
                        className={`inline-flex items-center gap-1.5 ${showAbbr ? "pl-1.5 pr-3" : "p-1.5"} py-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-wider rounded-lg border transition-colors ${active ? "text-white" : "bg-black/25 border-white/15 text-[#94A3B8] hover:text-white"}`}
                    >
                        <TeamBadge teamId={t} size="xs" asLink={false} />
                        {showAbbr && (TEAM_INFO[t]?.abbr || TEAM_INFO[t]?.abbreviation || t.toUpperCase())}
                    </button>
                );
            })}
        </div>
    );
}

export default PwhlTeamButtons;
